import { get } from 'lodash'
import React from 'react'
import Highlighter from 'react-highlight-words'
import styled from 'styled-components'

// @ts-expect-error: no types in this version of @gnomad/ui
import { TextButton } from '@gnomad/ui'
import { GeneResultColumnGroup, VariantColumnConfig } from '../Browser'
import {
  DEFAULT_VARIANT_CATEGORY_COLOR,
  DEFAULT_VARIANT_CATEGORY_OPTIONS,
  VariantCategoryOption,
} from '../variantCategories'
import { FilterState } from './VariantFilterControls'

const ColumnHeadingLabel = styled.div<{ $isCentered?: boolean }>`
  text-align: ${({ $isCentered }) => ($isCentered ? 'center' : 'left')};
`

const VariantIdButton = styled(TextButton)`
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const VariantCategoryMarker = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 0.5em;

  &::before {
    content: '';
    display: inline-block;
    box-sizing: border-box;
    width: 10px;
    height: 10px;
    border: 1px solid #000;
    border-radius: 5px;
    background: ${(props) => props.color};
  }
`

interface GroupResult {
  ac_case: number
  an_case: number
  af_case: number
  ac_ctrl: number
  an_ctrl: number
  af_ctrl: number
  af: number
  [key: string]: number | null
}

export interface VariantRow {
  variant_id: string
  annotationRowId?: string
  pos: number
  consequence: string
  consequenceCategory: string
  hgvsp: string
  hgvsc: string
  group_results: { [key: string]: GroupResult }
  info: any
  [key: string]: any
}

// TK: TODO: fixme, use variant row! type with all these things
interface Variant {
  variant_id: string
}

const formatNumberCell = (number: any): string => {
  if (number === null || number === undefined) {
    return ''
  }
  const truncated = Number(number.toPrecision(3))
  if (truncated === 0) {
    return '0'
  }
  return truncated.toString()
}

const renderNumberCell = (row: VariantRow, key: string): string => formatNumberCell(get(row, key))

const renderExponentialNumberCell = (row: VariantRow, key: string): string => {
  const number = get(row, key)
  if (number === null || number === undefined) {
    return ''
  }
  const truncated = Number(number.toPrecision(3))
  if (truncated === 0) {
    return '0'
  }
  return truncated.toExponential(2)
}

export const renderExponentialIfSmall = (number: number | null | undefined): string => {
  if (number === null || number === undefined) {
    return ''
  }

  const truncated = Number(number.toPrecision(3))
  if (truncated === 0) {
    return '0'
  }

  if (truncated < 0.01) {
    return truncated.toExponential(2)
  }

  return truncated.toString()
}

const renderExponentialNumberCellIfSmall = (row: VariantRow, key: string) => {
  const number = get(row, key)
  return renderExponentialIfSmall(number)
}

interface RenderContext {
  highlightWords?: string[]
  onClickVariant?: (row: VariantRow) => void
}

export interface VariantTableColumn {
  centerCellContent?: boolean
  centerNumericValues?: boolean
  group?: GeneResultColumnGroup
  key: string
  heading: React.ReactNode
  csvHeading?: string
  tooltip?: string
  isRowHeader?: boolean
  isSortable: boolean
  sortFunction: (a: any, b: any) => number
  sortKey: string
  minWidth: number
  grow?: number
  render: (row: VariantRow, key: string, context: RenderContext) => React.ReactNode
  renderForCSV: (row: VariantRow, key: string) => string | number | null
}

const buildVariantDescriptionColumns = (
  categoryColors: Record<string, string>
): VariantTableColumn[] => {
  const hgvsColumnDefinitions = [
    {
      key: 'hgvs',
      heading: 'HGVSp/c',
      tooltip: 'HGVS protein (if available) or coding sequence',
    },
  ]

  return [
    {
      key: 'variant_id',
      heading: 'Variant ID',
      tooltip: 'Chromosome-position-reference-alternate',
      isRowHeader: true,
      isSortable: true,
      sortFunction: (a, b) => a - b,
      sortKey: 'pos',
      minWidth: 150,
      grow: 2,
      render: (row, key, { highlightWords = [], onClickVariant }) => (
        <VariantIdButton onClick={() => onClickVariant?.(row)} tabIndex={-1}>
          <Highlighter searchWords={highlightWords} textToHighlight={row[key]} />
        </VariantIdButton>
      ),
      renderForCSV: get,
    },
    ...hgvsColumnDefinitions.map(
      ({ key, heading, tooltip }): VariantTableColumn => ({
        key,
        heading,
        tooltip,
        isSortable: true,
        sortFunction: (a, b) => a.localeCompare(b),
        sortKey: key,
        minWidth: 130,
        grow: 2,
        render: (row, columnKey, { highlightWords = [] }) => (
          <Highlighter
            className="grid-cell-content"
            searchWords={highlightWords}
            textToHighlight={row[columnKey] || ''}
          />
        ),
        renderForCSV: get,
      })
    ),
    {
      key: 'consequence',
      heading: 'Consequence',
      tooltip: 'Predicted functional consequence',
      isSortable: true,
      sortFunction: (a, b) => a.localeCompare(b),
      sortKey: 'consequence',
      minWidth: 180,
      render: (row, _key, { highlightWords = [] }) =>
        row.consequence && (
          <span className="grid-cell-content">
            <VariantCategoryMarker
              color={categoryColors[row.consequenceCategory] || DEFAULT_VARIANT_CATEGORY_COLOR}
            />
            <Highlighter searchWords={highlightWords} textToHighlight={row.consequence || ''} />
          </span>
        ),
      renderForCSV: get,
    },
  ]
}

const statColumns: VariantTableColumn[] = [
  {
    key: 'group_result.ac_case',
    heading: 'AC Case',
    tooltip: 'Allele count in cases',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.ac_case',
    minWidth: 75,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.an_case',
    heading: 'AN Case',
    tooltip: 'Allele number in cases',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.an_case',
    minWidth: 75,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.ac_ctrl',
    heading: 'AC Control',
    tooltip: 'Allele count in controls',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.ac_ctrl',
    minWidth: 75,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.an_ctrl',
    heading: 'AN Control',
    tooltip: 'Allele number in controls',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.an_ctrl',
    minWidth: 75,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.af_case',
    heading: 'AF Case',
    tooltip: 'Allele frequency in cases',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.af_case',
    minWidth: 80,
    render: (row, key) => renderExponentialNumberCell(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.af_ctrl',
    heading: 'AF Control',
    tooltip: 'Allele frequency in controls',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.af_ctrl',
    minWidth: 80,
    render: (row, key) => renderExponentialNumberCell(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
]

const gp2StatColumns: VariantTableColumn[] = [
  // ---
  {
    key: 'group_result.wgs_ac_pd',
    heading: 'WGS AC PD',
    tooltip: 'Allele count in PD in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_ac_pd',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.wgs_an_pd',
    heading: 'WGS AN PD',
    tooltip: 'Allele number in PD in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_an_pd',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.wgs_af_pd',
    heading: 'WGS AF PD',
    tooltip: 'Allele frequency in PD in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_af_pd',
    minWidth: 80,
    render: (row, key) => renderExponentialNumberCellIfSmall(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  // ---
  {
    key: 'group_result.wgs_ac_psp',
    heading: 'WGS AC PSP',
    tooltip: 'Allele count in PSP in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_ac_psp',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.wgs_an_psp',
    heading: 'WGS AN PSP',
    tooltip: 'Allele number in PSP in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_an_psp',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.wgs_af_psp',
    heading: 'WGS AF PSP',
    tooltip: 'Allele frequency in PSP in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_af_psp',
    minWidth: 80,
    render: (row, key) => renderExponentialNumberCellIfSmall(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  // ---
  {
    key: 'group_result.wgs_ac_dlb',
    heading: 'WGS AC DLB',
    tooltip: 'Allele count in DLB in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_ac_dlb',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.wgs_an_dlb',
    heading: 'WGS AN DLB',
    tooltip: 'Allele number in DLB in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_an_dlb',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.wgs_af_dlb',
    heading: 'WGS AF DLB',
    tooltip: 'Allele frequency in DLB in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_af_dlb',
    minWidth: 80,
    render: (row, key) => renderExponentialNumberCellIfSmall(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  // ---
  {
    key: 'group_result.wgs_ac_msa',
    heading: 'WGS AC MSA',
    tooltip: 'Allele count in MSA in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_ac_msa',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.wgs_an_msa',
    heading: 'WGS AN MSA',
    tooltip: 'Allele number in MSA in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_an_msa',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.wgs_af_msa',
    heading: 'WGS AF MSA',
    tooltip: 'Allele frequency in MSA in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_af_msa',
    minWidth: 80,
    render: (row, key) => renderExponentialNumberCellIfSmall(row, key),
    renderForCSV: (row, key) => get(row, key),
  },

  {
    key: 'group_result.wgs_ac_ctrl',
    heading: 'WGS AC Control',
    tooltip: 'Allele count in controls in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_ac_ctrl',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  // ---
  {
    key: 'group_result.wgs_an_ctrl',
    heading: 'WGS AN Control',
    tooltip: 'Allele number in controls in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_an_ctrl',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.wgs_af_ctrl',
    heading: 'WGS AF Control',
    tooltip: 'Allele frequency in controls in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_af_ctrl',
    minWidth: 80,
    render: (row, key) => renderExponentialNumberCellIfSmall(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  // ---
  {
    key: 'group_result.wgs_ac_other',
    heading: 'WGS AC Other',
    tooltip: 'Allele count in others in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_ac_other',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.wgs_an_other',
    heading: 'WGS AN Other',
    tooltip: 'Allele number in others in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_an_other',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.wgs_af_other',
    heading: 'WGS AF Other',
    tooltip: 'Allele frequency in others in whole genome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.wgs_af_other',
    minWidth: 80,
    render: (row, key) => renderExponentialNumberCellIfSmall(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  // ---
  {
    key: 'group_result.ces_ac_pd',
    heading: 'CES AC PD',
    tooltip: 'Allele count in PD in clinical exome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.ces_ac_pd',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.ces_an_pd',
    heading: 'CES AN PD',
    tooltip: 'Allele number in PD in clinical exome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.ces_an_pd',
    minWidth: 80,
    render: (row, key) => get(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
  {
    key: 'group_result.ces_af_pd',
    heading: 'CES AF PD',
    tooltip: 'Allele frequency in PD in clinical exome sequencing',
    isSortable: true,
    sortFunction: (a, b) => a - b,
    sortKey: 'group_result.ces_af_pd',
    minWidth: 80,
    render: (row, key) => renderExponentialNumberCellIfSmall(row, key),
    renderForCSV: (row, key) => get(row, key),
  },
]

type GetVariantTableColumnsProps = {
  datasetId: string
  variantResultColumns: VariantColumnConfig[]
  filter: FilterState
  variantCategoryOptions?: VariantCategoryOption[]
}

const getVariantTableColumns = ({
  datasetId,
  variantResultColumns,
  filter,
  variantCategoryOptions = DEFAULT_VARIANT_CATEGORY_OPTIONS,
}: GetVariantTableColumnsProps): VariantTableColumn[] => {
  const categoryColors = Object.fromEntries(
    variantCategoryOptions.map((option) => [option.id, option.color])
  )
  const datasetColumns = [...buildVariantDescriptionColumns(categoryColors)]

  if (datasetId === 'GP2') {
    let renderedGP2Columns = gp2StatColumns

    const allGP2CaseColumnGroups = Object.keys(filter.gp2VariantColumnGroups!)
    const activeGP2CaseColumnGroups = allGP2CaseColumnGroups.filter(
      (g) => filter.gp2VariantColumnGroups![g]
    )
    renderedGP2Columns = gp2StatColumns.filter((col) => {
      const suffix = col.key.split('_').pop() as string

      if (allGP2CaseColumnGroups.includes(suffix)) {
        return activeGP2CaseColumnGroups.includes(suffix)
      }

      return true
    })

    datasetColumns.push(...renderedGP2Columns)
  } else if (datasetId !== 'AGB') {
    datasetColumns.push(...statColumns)
  }

  const filteredVariantResultColumns =
    datasetId === 'AGB' && filter.agbVariantColumnGroups
      ? variantResultColumns.filter(
          (column) => !column.group || filter.agbVariantColumnGroups![column.group.key]
        )
      : variantResultColumns

  const resultColumns: VariantTableColumn[] = filteredVariantResultColumns.map((column) => {
    const getValue = column.accessor ?? ((row: any) => get(row, column.key))

    const plainHeading = column.heading || column.key
    const alignedHeading = (
      <ColumnHeadingLabel $isCentered={column.centerHeading}>{plainHeading}</ColumnHeadingLabel>
    )

    return {
      key: column.key,
      group: column.group,
      heading: column.group || column.centerHeading ? alignedHeading : plainHeading,
      csvHeading: plainHeading,
      centerCellContent: column.centerCellContent,
      centerNumericValues: column.centerNumericValues,
      tooltip: column.tooltip,
      isSortable: column.isSortable !== false,
      sortFunction: column.sortFunction || ((a, b) => a - b),
      sortKey: column.sortKey || column.key,
      minWidth: column.minWidth || 65,
      render: column.render
        ? (row) => column.render!(getValue(row))
        : (row) => formatNumberCell(getValue(row)),
      renderForCSV: column.renderForCSV
        ? (row) => column.renderForCSV!(getValue(row))
        : (row) => getValue(row),
    }
  })

  const variantTableColumns = [...datasetColumns, ...resultColumns]

  return variantTableColumns
}

export default getVariantTableColumns
